import { LOCKED_BASIC_FIELDS } from '@features/forms/constants';
import { FormFieldData, MemberFieldKey } from '@features/forms/types';

import {
  getCatalogEntry,
  getMemberCatalogFieldOptions,
  mapCatalogEntryToFormField,
  MEMBER_FIELD_CATALOG,
  MEMBER_FIELD_PICKER,
  MemberFieldCatalogEntry,
  MemberFieldPickerGroup
} from '../constants';
import { assignFormFieldDisplayOrders } from './fieldDisplayOrder';
import {
  buildMemberCustomFieldCatalogEntries,
  mapCustomCatalogEntryToFormField,
  MemberCustomFieldCatalogEntry
} from './mapMemberCustomFieldType';

export type MemberFieldPickerItemState = {
  enabled: boolean;
  required: boolean;
};

export type CatalogMemberFieldKey =
  (typeof MEMBER_FIELD_CATALOG)[number]['key'];

export type MemberFieldPickerState = Record<string, MemberFieldPickerItemState>;

export type { MemberCustomFieldCatalogEntry, MemberFieldPickerGroup };

export const DEFAULT_PICKER_ITEM_STATE: MemberFieldPickerItemState = {
  enabled: false,
  required: false
};

export const normalizeFieldLabel = (label: string) =>
  label.trim().toLowerCase().replace(/\s+/g, ' ');

const MEMBER_CUSTOM_TO_CATALOG_KEY: Partial<
  Record<string, CatalogMemberFieldKey>
> = {
  date_of_birth: 'dob',
  alternate_number: 'alternate_mobile'
};

const getReservedMemberFieldLabels = (): Set<string> => {
  const labels = [
    ...MEMBER_FIELD_CATALOG.map((entry) => normalizeFieldLabel(entry.label)),
    ...LOCKED_BASIC_FIELDS.map((field) => normalizeFieldLabel(field.label))
  ];

  return new Set(labels);
};

const hasDuplicateCustomMemberFieldLabel = (
  entry: MemberCustomFieldCatalogEntry,
  customCatalog: MemberCustomFieldCatalogEntry[]
): boolean => {
  const normalizedLabel = normalizeFieldLabel(entry.label);
  const matchingEntries = customCatalog.filter(
    (customEntry) => normalizeFieldLabel(customEntry.label) === normalizedLabel
  );

  return matchingEntries.length > 1 && matchingEntries[0].key !== entry.key;
};

export const isCustomMemberFieldPickerDisabled = (
  entry: MemberCustomFieldCatalogEntry,
  customCatalog: MemberCustomFieldCatalogEntry[] = []
): boolean => {
  if (MEMBER_CUSTOM_TO_CATALOG_KEY[entry.key]) {
    return true;
  }

  if (
    MEMBER_FIELD_CATALOG.some((catalogEntry) => catalogEntry.key === entry.key)
  ) {
    return true;
  }

  const normalizedLabel = normalizeFieldLabel(entry.label);
  if (getReservedMemberFieldLabels().has(normalizedLabel)) {
    return true;
  }

  return hasDuplicateCustomMemberFieldLabel(entry, customCatalog);
};

const matchesBasicFieldLabel = (normalizedLabel: string) =>
  LOCKED_BASIC_FIELDS.some(
    (field) => normalizeFieldLabel(field.label) === normalizedLabel
  );

export const getCustomMemberFieldPickerDisabledTooltip = (
  entry: MemberCustomFieldCatalogEntry,
  customCatalog: MemberCustomFieldCatalogEntry[] = []
): string | undefined => {
  if (!isCustomMemberFieldPickerDisabled(entry, customCatalog)) {
    return undefined;
  }

  const { disabledCustomFieldTooltips } = MEMBER_FIELD_PICKER;

  if (
    MEMBER_CUSTOM_TO_CATALOG_KEY[entry.key] ||
    MEMBER_FIELD_CATALOG.some((catalogEntry) => catalogEntry.key === entry.key)
  ) {
    return disabledCustomFieldTooltips.catalogKey;
  }

  const normalizedLabel = normalizeFieldLabel(entry.label);

  if (matchesBasicFieldLabel(normalizedLabel)) {
    return disabledCustomFieldTooltips.basicLabel;
  }

  if (
    MEMBER_FIELD_CATALOG.some(
      (catalogEntry) =>
        normalizeFieldLabel(catalogEntry.label) === normalizedLabel
    )
  ) {
    return disabledCustomFieldTooltips.catalogLabel;
  }

  if (hasDuplicateCustomMemberFieldLabel(entry, customCatalog)) {
    return disabledCustomFieldTooltips.duplicateCustomLabel;
  }

  return disabledCustomFieldTooltips.catalogLabel;
};

export const getMemberFieldLabel = (field: FormFieldData) => {
  if (field.memberFieldKey) {
    return getCatalogEntry(field.memberFieldKey)?.label ?? field.label;
  }
  return field.label;
};

const dedupeMemberFieldsByLabel = (
  fields: FormFieldData[]
): FormFieldData[] => {
  const seenLabels = new Set<string>();

  return fields.filter((field) => {
    const label = normalizeFieldLabel(getMemberFieldLabel(field));
    if (seenLabels.has(label)) {
      return false;
    }
    seenLabels.add(label);
    return true;
  });
};

export const getPickerRowState = (
  pickerState: MemberFieldPickerState,
  key: string,
  defaultRequired = false
): MemberFieldPickerItemState =>
  pickerState[key] ?? {
    ...DEFAULT_PICKER_ITEM_STATE,
    required: defaultRequired
  };

const getFieldPickerKey = (field: FormFieldData) =>
  field.memberFieldKey ?? field.memberCustomFieldKey;

const resolveMemberFieldPickerKey = (
  field: FormFieldData,
  customCatalog: MemberCustomFieldCatalogEntry[]
): string | undefined => {
  const directKey = getFieldPickerKey(field);

  if (directKey) {
    const isCatalogKey = MEMBER_FIELD_CATALOG.some(
      (entry) => entry.key === directKey
    );
    if (isCatalogKey) {
      return directKey;
    }

    const customEntry = customCatalog.find((entry) => entry.key === directKey);
    if (
      !customEntry ||
      !isCustomMemberFieldPickerDisabled(customEntry, customCatalog)
    ) {
      return directKey;
    }
  }

  const normalizedLabel = normalizeFieldLabel(getMemberFieldLabel(field));
  const catalogMatch = MEMBER_FIELD_CATALOG.find(
    (entry) => normalizeFieldLabel(entry.label) === normalizedLabel
  );
  if (catalogMatch) {
    return catalogMatch.key;
  }

  const customMatch = customCatalog.find(
    (entry) =>
      normalizeFieldLabel(entry.label) === normalizedLabel &&
      !isCustomMemberFieldPickerDisabled(entry, customCatalog)
  );

  return customMatch?.key;
};

export const resolveMemberFieldIdentity = (
  field: FormFieldData
): FormFieldData => {
  if (field.fieldCategory !== 'member') {
    return field;
  }

  const {
    memberFieldKey: existingMemberFieldKey,
    memberCustomFieldKey: existingCustomKey,
    options: existingOptions,
    label
  } = field;

  let memberFieldKey = existingMemberFieldKey;
  let memberCustomFieldKey = existingCustomKey;

  if (!memberFieldKey && !memberCustomFieldKey) {
    const normalizedApiName = label
      .trim()
      .toLowerCase()
      .replace(/-/g, '_')
      .replace(/\s+/g, '_');
    const catalogKeyFromApiName =
      MEMBER_CUSTOM_TO_CATALOG_KEY[normalizedApiName];
    if (catalogKeyFromApiName) {
      memberFieldKey = catalogKeyFromApiName;
    } else {
      const catalogMatch = MEMBER_FIELD_CATALOG.find(
        (entry) =>
          normalizeFieldLabel(entry.label) === normalizeFieldLabel(label)
      );
      if (catalogMatch) {
        memberFieldKey = catalogMatch.key;
      }
    }
  }

  if (
    memberCustomFieldKey &&
    MEMBER_CUSTOM_TO_CATALOG_KEY[memberCustomFieldKey]
  ) {
    memberFieldKey = MEMBER_CUSTOM_TO_CATALOG_KEY[memberCustomFieldKey];
    memberCustomFieldKey = undefined;
  }

  let options = existingOptions;
  if (memberFieldKey && !options?.length) {
    options = getMemberCatalogFieldOptions(memberFieldKey);
  }

  const resolved: FormFieldData = {
    ...field,
    ...(memberFieldKey ? { memberFieldKey } : {}),
    ...(options?.length ? { options } : {})
  };

  if (memberCustomFieldKey) {
    resolved.memberCustomFieldKey = memberCustomFieldKey;
  } else {
    delete resolved.memberCustomFieldKey;
  }

  return resolved;
};

export const getMemberFieldPickerKey = (
  field: FormFieldData,
  customCatalog: MemberCustomFieldCatalogEntry[] = []
): string | undefined => {
  const resolved =
    field.fieldCategory === 'member'
      ? resolveMemberFieldIdentity(field)
      : field;

  return resolveMemberFieldPickerKey(resolved, customCatalog);
};

export const enrichMemberFormFields = (
  fields: FormFieldData[]
): FormFieldData[] => fields.map(resolveMemberFieldIdentity);

export const buildPickerStateFromMemberFields = (
  memberFields: FormFieldData[],
  customCatalog: MemberCustomFieldCatalogEntry[] = []
): MemberFieldPickerState => {
  const state: MemberFieldPickerState = {};

  MEMBER_FIELD_CATALOG.forEach((entry) => {
    state[entry.key] = { enabled: false, required: entry.required };
  });

  customCatalog.forEach((entry) => {
    state[entry.key] = { enabled: false, required: false };
  });

  memberFields.forEach((field) => {
    const key = resolveMemberFieldPickerKey(field, customCatalog);
    if (!key || !state[key]) return;

    state[key] = { enabled: true, required: field.required };
  });

  return state;
};

export const sortMemberFieldsByCatalog = (
  memberFields: FormFieldData[]
): FormFieldData[] => {
  const getMemberFieldSortOrder = (field: FormFieldData) => {
    if (field.memberFieldKey) {
      const entry = MEMBER_FIELD_CATALOG.find(
        (catalogEntry) => catalogEntry.key === field.memberFieldKey
      );
      return entry?.order ?? Number.MAX_SAFE_INTEGER;
    }

    return field.order;
  };

  return [...memberFields].sort(
    (left, right) =>
      getMemberFieldSortOrder(left) - getMemberFieldSortOrder(right)
  );
};

export const buildMemberFieldsFromPickerState = (
  pickerState: MemberFieldPickerState,
  customCatalog: MemberCustomFieldCatalogEntry[] = [],
  existingMemberFields: FormFieldData[] = []
): FormFieldData[] => {
  const catalogFields = MEMBER_FIELD_CATALOG.filter(
    (entry) => pickerState[entry.key]?.enabled
  ).map((entry) =>
    mapCatalogEntryToFormField(
      entry,
      pickerState[entry.key]?.required ?? entry.required
    )
  );

  const customFields = customCatalog
    .filter((entry) => pickerState[entry.key]?.enabled)
    .filter((entry) => !isCustomMemberFieldPickerDisabled(entry, customCatalog))
    .map((entry) =>
      mapCustomCatalogEntryToFormField(
        entry,
        pickerState[entry.key]?.required ?? entry.required
      )
    );

  const built = dedupeMemberFieldsByLabel(
    sortMemberFieldsByCatalog([...catalogFields, ...customFields])
  );

  const existingByKey = new Map<string, FormFieldData>();
  existingMemberFields.forEach((field) => {
    const key = getMemberFieldPickerKey(field, customCatalog);
    if (key) {
      existingByKey.set(key, field);
    }
  });

  return built.map((field) => {
    const key = getMemberFieldPickerKey(field, customCatalog);
    if (!key) {
      return field;
    }

    const existing = existingByKey.get(key);
    if (!existing) {
      return field;
    }

    return {
      ...field,
      id: existing.id,
      required: field.required
    };
  });
};

export const mergeMemberFieldsIntoWizard = (
  allFields: FormFieldData[],
  memberFields: FormFieldData[],
  customCatalog: MemberCustomFieldCatalogEntry[] = []
): FormFieldData[] => {
  const nextMemberFields = sortMemberFieldsByCatalog(memberFields);
  const memberKeys = new Set(
    nextMemberFields
      .map((field) => getMemberFieldPickerKey(field, customCatalog))
      .filter((key): key is string => Boolean(key))
  );

  const nonMember = allFields.filter((field) => {
    if (field.fieldCategory === 'member') {
      return false;
    }

    const key = getMemberFieldPickerKey(field, customCatalog);
    return !(key && memberKeys.has(key));
  });

  return assignFormFieldDisplayOrders([...nonMember, ...nextMemberFields]);
};

export const getCatalogEntriesByGroup = (
  group: MemberFieldPickerGroup
): MemberFieldCatalogEntry[] =>
  MEMBER_FIELD_CATALOG.filter((entry) => entry.group === group);

export const removeMemberFieldByKey = (
  allFields: FormFieldData[],
  key: MemberFieldKey
) => allFields.filter((field) => field.memberFieldKey !== key);

export const removeMemberFieldByCustomKey = (
  allFields: FormFieldData[],
  key: string
) => allFields.filter((field) => field.memberCustomFieldKey !== key);

export { buildMemberCustomFieldCatalogEntries };
